/**
 * Eine automatische Sicherung in den gewaehlten Ordner schreiben (F-46).
 *
 * Reihenfolge mit Absicht: erst die Datei schreiben und ihre Groesse pruefen,
 * DANN aufraeumen und die Sicherung als erledigt merken. Scheitert das
 * Schreiben, ist nichts geloescht und die Erinnerung laeuft weiter.
 *
 * Kein `parseBackup` und kein zod hier: Diese Datei haengt am Hauptbuendel.
 */
import { zuletztLaden, zuletztSpeichern } from '@/db/repositories/autoSicherung';
import { aufraeumListe, autoDateiname, istAutoDatei, schutzPruefen, type AutoZuletzt } from '@/domain/sicherung/autoSicherung';
import { toLocalDateTime } from '@/lib/datetime';
import { backupJson, buildBackup, markExported } from '@/lib/backup/export';
import { totalRows } from '@/lib/backup/format';
import { dateienAuflisten, dateienLoeschen, dateiSchreiben, type OrdnerDatei, type OrdnerHandle } from '@/lib/backup/ordner';

export interface AutoErgebnis extends AutoZuletzt {
  eintraege: number;
  /** Beim Aufraeumen entfernte alte Sicherungen. */
  geloescht: string[];
}

export async function autoSicherungSchreiben(
  ordner: OrdnerHandle,
  opts: { behalten: number; jetzt?: Date },
): Promise<AutoErgebnis> {
  const vorher = await zuletztLaden();
  const backup = await buildBackup();
  const eintraege = totalRows(backup.counts);
  const datei = autoDateiname(toLocalDateTime(opts.jetzt ?? new Date()));

  // Wirft bei jedem Problem - dann ist weder aufgeraeumt noch etwas gemerkt.
  const bytes = await dateiSchreiben(ordner, datei, backupJson(backup));

  // Ordnerinhalt fuers Aufraeumen und als Mass, falls kein Vergleichswert gespeichert ist.
  let dateien: OrdnerDatei[] | null = null;
  try {
    dateien = await dateienAuflisten(ordner);
  } catch (err) {
    console.warn('Ordner nicht gelesen - aufgeraeumt wird beim naechsten Mal:', err);
  }
  const andere = (dateien ?? []).filter((d) => d.name !== datei && istAutoDatei(d.name) && d.bytes > 0).map((d) => d.bytes);
  // Deutlich weniger als der letzte gute Stand: neue Datei behalten, alte auch.
  const schutz = schutzPruefen({ eintraege, bytes }, vorher, andere.length ? Math.max(...andere) : null);

  let geloescht: string[] = [];
  if (!schutz.geschuetzt && dateien) {
    try {
      // Die eben geschriebene Datei steht nie auf der Liste.
      geloescht = aufraeumListe(dateien, opts.behalten, datei);
      await dateienLoeschen(ordner, geloescht);
    } catch (err) {
      // Die Sicherung selbst liegt im Ordner; Aufraeumen klappt beim naechsten Mal.
      console.warn('Alte Sicherungen nicht aufgeräumt:', err);
      geloescht = [];
    }
  }

  const zuletzt: AutoZuletzt = {
    at: backup.exportedAt, datei, bytes, eintraege,
    ...(schutz.referenz !== undefined ? { referenz: schutz.referenz } : {}),
    ...(schutz.referenzBytes !== undefined ? { referenzBytes: schutz.referenzBytes } : {}),
    ...(schutz.geschuetzt ? { geschuetzt: true } : {}),
  };
  await zuletztSpeichern(zuletzt);
  // Zaehlt fuer die Erinnerung wie eine Sicherung von Hand.
  await markExported(backup.exportedAt);
  return { ...zuletzt, eintraege, geloescht };
}
